import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Paperclip, Loader2, FileText, Bell, BellRing } from "lucide-react";
import { pushSupported, getPushSubscription, enablePush as enablePushApi } from "@/lib/pwa";

const C = { navy: "#121b28", sand: "#f4ebd7", terra: "#e37e5e", olive: "#8a9771" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  try {
    const d = new Date(iso); const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

export default function ClientChatWidget({ token }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [clientName, setClientName] = useState("");
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const fileRef = useRef(null);
  const bottomRef = useRef(null);
  const [highlightId, setHighlightId] = useState(null);
  const openedViaNotifRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${BACKEND}/api/public/trip/${token}/chat`);
      setClientName(data.client_name || "");
      setMessages(data.messages || []);
      setUnread(data.unread_client || 0);
    } catch (e) { /* silencioso */ }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, open ? 4000 : 9000);
    return () => clearInterval(id);
  }, [open, load]);

  // Al abrir el chat: marcar como leído + limpiar la burbuja del icono de la app.
  useEffect(() => {
    if (!open) return;
    setUnread(0);
    axios.post(`${BACKEND}/api/public/trip/${token}/chat/read`).catch(() => {});
    if (typeof navigator !== "undefined" && navigator.clearAppBadge) navigator.clearAppBadge().catch(() => {});
  }, [open, token]);
  useEffect(() => { if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, open]);

  // Auto-abrir el chat al venir de una notificación (deep-link ?chat=1 o mensaje del Service Worker).
  useEffect(() => {
    let viaUrl = false;
    try { viaUrl = new URLSearchParams(window.location.search).get("chat") === "1"; } catch (_) {}
    if (viaUrl) {
      setOpen(true); openedViaNotifRef.current = true;
      try { const u = new URL(window.location.href); u.searchParams.delete("chat"); window.history.replaceState({}, "", u.pathname + u.search + u.hash); } catch (_) {}
    }
    const onMsg = (e) => { if (e.data && e.data.type === "et-open-chat") { setOpen(true); openedViaNotifRef.current = true; } };
    if ("serviceWorker" in navigator) navigator.serviceWorker.addEventListener("message", onMsg);
    return () => { if ("serviceWorker" in navigator) navigator.serviceWorker.removeEventListener("message", onMsg); };
  }, []);

  // Al abrir desde una notificación, resalta el último mensaje del equipo.
  useEffect(() => {
    if (!openedViaNotifRef.current || !open || messages.length === 0) return undefined;
    const staff = [...messages].reverse().find((m) => m.sender !== "client" && m.sender !== "system");
    if (!staff) return undefined;
    setHighlightId(staff.id);
    openedViaNotifRef.current = false;
    const t = setTimeout(() => setHighlightId(null), 4500);
    return () => clearTimeout(t);
  }, [open, messages]);

  const send = async () => {
    if ((!text.trim() && !file) || sending) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("text", text);
      if (file) fd.append("file", file);
      await axios.post(`${BACKEND}/api/public/trip/${token}/chat`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText(""); setFile(null);
      await load();
    } catch (e) { /* noop */ }
    finally { setSending(false); }
  };

  const openAttachment = (m) => window.open(`${BACKEND}/api/public/trip/${token}/chat/attachment/${m.id}`, "_blank");

  // ----- Web Push (PWA): suscripción a notificaciones cuando el staff responde -----
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState("");

  useEffect(() => {
    getPushSubscription().then((sub) => setPushOn(!!sub));
  }, []);

  const onEnablePush = async () => {
    if (pushBusy) return;
    if (pushOn) { setPushMsg("Notifications are on. We'll alert you when we reply."); return; }
    if (!pushSupported()) { setPushMsg("Notifications aren't supported on this browser."); return; }
    setPushBusy(true); setPushMsg("");
    const res = await enablePushApi(token);
    if (res.ok) { setPushOn(true); setPushMsg("All set! 🔔 You'll get a notification when we reply."); }
    else if (res.reason === "ios-install") setPushMsg("Install the app first: tap Share → Add to Home Screen, then turn on alerts here.");
    else if (res.reason === "denied") setPushMsg("Notifications are blocked. Enable them in your browser/app settings.");
    else if (res.reason === "unsupported") setPushMsg("Notifications aren't supported on this browser.");
    else setPushMsg("Couldn't enable notifications. Please try again.");
    setPushBusy(false);
  };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="client-chat-launcher"
          className="fixed bottom-24 right-5 z-[60] flex h-14 w-14 items-center justify-center rounded-full shadow-lg ring-1 ring-black/5 transition-transform hover:scale-105 print:hidden"
          style={{ backgroundColor: C.navy, color: "#fff" }} aria-label="Chat with us">
          <MessageCircle className="h-6 w-6" />
          {unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full border-2 border-white px-1 text-[11px] font-bold text-white"
              style={{ backgroundColor: "#dc2626" }} data-testid="client-chat-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-5 z-[60] flex h-[70vh] max-h-[560px] w-[92vw] max-w-[380px] flex-col overflow-hidden rounded-2xl shadow-2xl print:hidden"
          style={{ backgroundColor: "#fff" }} data-testid="client-chat-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: C.navy, color: C.sand }}>
            <div>
              <p className="text-sm font-semibold" style={{ fontFamily: "'Kanit', sans-serif" }}>Chat with Espíritu Travel</p>
              <p className="text-[11px] opacity-70">We usually reply within working hours (Mon–Fri)</p>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={onEnablePush} disabled={pushBusy} data-testid="client-chat-push-toggle"
                title={pushOn ? "Notifications on" : "Turn on notifications"}
                className="rounded-full p-1.5 hover:bg-white/10 disabled:opacity-50">
                {pushBusy ? <Loader2 className="h-5 w-5 animate-spin" /> : pushOn ? <BellRing className="h-5 w-5" style={{ color: C.olive }} /> : <Bell className="h-5 w-5" />}
              </button>
              <button onClick={() => setOpen(false)} data-testid="client-chat-close" className="rounded-full p-1 hover:bg-white/10"><X className="h-5 w-5" /></button>
            </div>
          </div>

          {/* Aviso push (inline, en inglés) */}
          {pushMsg && (
            <div className="flex items-start justify-between gap-2 px-3 py-2 text-[11px]" style={{ backgroundColor: C.sand, color: C.navy }} data-testid="client-chat-push-hint">
              <span className="flex items-center gap-1.5"><Bell className="h-3.5 w-3.5 shrink-0" style={{ color: C.olive }} />{pushMsg}</span>
              <button onClick={() => setPushMsg("")} className="shrink-0 opacity-50 hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
            </div>
          )}

          {/* Mensajes */}
          <div className="flex-1 space-y-2.5 overflow-y-auto bg-stone-50 p-3" data-testid="client-chat-messages">
            {messages.length === 0 && (
              <div className="mx-auto mt-6 max-w-[85%] rounded-xl border bg-white p-3 text-center text-xs text-stone-500">
                👋 Hi{clientName ? ` ${clientName.split(" ")[0]}` : ""}! Ask us anything about your trip — we're here to help.
              </div>
            )}
            {messages.map((m) => {
              if (m.sender === "system") {
                return <div key={m.id} className="mx-auto max-w-[90%] rounded-lg bg-amber-50 px-3 py-2 text-center text-[11px] text-amber-800" data-testid="client-chat-system">{m.text}</div>;
              }
              const mine = m.sender === "client";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm transition-shadow ${m.id === highlightId ? "ring-2 ring-amber-400 ring-offset-2 ring-offset-stone-50" : ""}`}
                    data-testid={m.id === highlightId ? "client-chat-msg-highlight" : undefined}
                    style={mine ? { backgroundColor: C.navy, color: "#fff", borderTopRightRadius: 4 }
                                : { backgroundColor: "#fff", color: C.navy, border: "1px solid #e7e5e4", borderTopLeftRadius: 4 }}>
                    {!mine && <p className="mb-0.5 text-[10px] font-semibold opacity-70">{m.author_name}</p>}
                    {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                    {m.attachment && (
                      <button onClick={() => openAttachment(m)} className="mt-1 flex items-center gap-1.5 rounded-md bg-black/5 px-2 py-1 text-[11px] hover:bg-black/10" data-testid={`client-chat-attach-${m.id}`}>
                        <FileText className="h-3.5 w-3.5" /><span className="truncate">{m.attachment.filename}</span>
                      </button>
                    )}
                    <p className={`mt-0.5 text-right text-[9px] ${mine ? "opacity-80" : "text-stone-400"}`}>{fmt(m.created_at)}</p>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {/* Composer */}
          <div className="border-t bg-white p-2.5">
            {file && <div className="mb-1.5 flex items-center gap-2 text-[11px] text-stone-500"><Paperclip className="h-3 w-3" /><span className="truncate">{file.name}</span><button onClick={() => setFile(null)}><X className="h-3 w-3" /></button></div>}
            <div className="flex items-end gap-1.5">
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="client-chat-file" />
              <button onClick={() => fileRef.current?.click()} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-stone-500 hover:bg-stone-100" title="Attach"><Paperclip className="h-4 w-4" /></button>
              <textarea rows={1} value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Write a message…" data-testid="client-chat-input"
                className="max-h-24 min-h-[38px] flex-1 resize-none rounded-2xl border border-stone-200 px-3 py-2 text-sm outline-none focus:border-stone-400" />
              <button onClick={send} disabled={(!text.trim() && !file) || sending} data-testid="client-chat-send"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white disabled:opacity-50" style={{ backgroundColor: C.navy }}>
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
